import { useCallback, useEffect, useState } from 'react';

/** Em que pé está uma leitura do backend. */
export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Recarrega mantendo o dado anterior na tela até o novo chegar. */
  reload: () => void;
}

function mensagem(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Falha ao carregar.';
}

/**
 * Carrega dados de uma função assíncrona e re-executa quando `deps` mudam.
 *
 * O padrão de fetch-em-useEffect aqui é aceitável: a `ConsoleDataPort` já é a
 * camada de acesso (com o adapter HTTP por trás), não há cache a compartilhar
 * entre telas, e cada tela quer o dado fresco ao abrir. `reload` cobre a
 * revalidação após uma escrita.
 *
 * A guarda `vivo` evita escrever estado de uma resposta que voltou depois de a
 * dependência mudar (ou o componente desmontar).
 */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: React.DependencyList,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError(null);
    fn()
      .then((resultado) => {
        if (vivo) setData(resultado);
      })
      .catch((erro: unknown) => {
        if (vivo) {
          setError(mensagem(erro));
          setData(null);
        }
      })
      .finally(() => {
        if (vivo) setLoading(false);
      });
    return () => {
      vivo = false;
    };
    // `fn` é recriada a cada render pelo chamador; as deps declaradas são a
    // fonte de verdade da re-execução, mais o `tick` do reload.
  }, [...deps, tick]);

  return { data, loading, error, reload };
}
